import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../services/auth';
import apiService from '../../services/api';
import { formatLargeNumber } from '../../utils/helpers';
import {
  Flame,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Info,
  Zap,
  Droplet,
  Factory,
  Package
} from 'lucide-react';
import './HotspotEmissions.css';

const formatEmissionValue = (value) => formatLargeNumber(value || 0, { decimals: 1 });

const CATEGORY_ICONS = {
  fuel_mobile_transport: Zap,
  natural_gas: Flame,
  electricity: Zap,
  binders: Droplet,
  additives: Package,
  miscellaneous: Factory
};

const CATEGORY_COLORS = {
  fuel_mobile_transport: '#F59E0B',
  natural_gas: '#EF4444',
  electricity: '#3B82F6',
  binders: '#8B5CF6',
  additives: '#10B981',
  miscellaneous: '#6B7280'
};

const HotspotEmissions = () => {
  const [searchParams] = useSearchParams();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [hotspotData, setHotspotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    loadScenarios();
  }, []);

  useEffect(() => {
    const scenarioId = searchParams.get('scenario');
    if (scenarioId && scenarios.length > 0) {
      const scenario = scenarios.find(s => s._id === scenarioId);
      if (scenario) {
        setSelectedScenario(scenario);
        loadHotspotData(scenarioId);
      }
    }
  }, [searchParams, scenarios]);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/asphalt/scenarios');
      const scenariosWithEmissions = response.data.filter(
        s => s.emissions && s.emissions.total_emissions > 0
      );
      setScenarios(scenariosWithEmissions);
      
      if (scenariosWithEmissions.length > 0 && !selectedScenario) {
        const firstScenario = scenariosWithEmissions[0];
        setSelectedScenario(firstScenario);
        loadHotspotData(firstScenario._id);
      }
    } catch (err) {
      console.error('Failed to load scenarios:', err);
      setError('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const loadHotspotData = async (scenarioId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(`/asphalt/scenarios/${scenarioId}/hotspot`);
      setHotspotData(response.data);
    } catch (err) {
      console.error('Failed to load hotspot data:', err);
      setError(err.response?.data?.detail || 'Failed to load hotspot emissions data');
    } finally {
      setLoading(false);
    }
  };

  const handleScenarioChange = (scenarioId) => {
    const scenario = scenarios.find(s => s._id === scenarioId);
    if (scenario) {
      setSelectedScenario(scenario);
      loadHotspotData(scenarioId);
    }
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  if (loading && !hotspotData) {
    return (
      <div className="hotspot-loading-container">
        <div className="loading-spinner loading-spinner-lg"></div>
        <span className="loading-text">Loading hotspot emissions data...</span>
      </div>
    );
  }

  if (error && !hotspotData) {
    return (
      <div className="hotspot-error-container">
        <AlertTriangle size={48} className="error-icon" />
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadScenarios}>
          Retry
        </button>
      </div>
    );
  }

  // Sort categories by total emissions (descending)
  const sortedCategories = hotspotData?.categories
    ? Object.entries(hotspotData.categories).sort((a, b) => b[1].total - a[1].total)
    : [];

  return (
    <div className="hotspot-emissions-page">
      {/* Header Section */}
      <div className="hotspot-header">
        <div className="header-content">
          <div className="title-section">
            <h1 className="page-title">
              <Flame className="title-icon" size={32} />
              Hotspot Emissions Analysis
            </h1>
            <p className="page-subtitle">
              Identify and analyze the largest sources of emissions across all lifecycle stages
            </p>
          </div>

          {/* Scenario Selector */}
          <div className="scenario-selector-container">
            <label htmlFor="scenario-select" className="selector-label">
              Select Scenario:
            </label>
            <select
              id="scenario-select"
              className="scenario-select"
              value={selectedScenario?._id || ''}
              onChange={(e) => handleScenarioChange(e.target.value)}
              disabled={loading}
            >
              {scenarios.map((scenario) => (
                <option key={scenario._id} value={scenario._id}>
                  {scenario.name} - {scenario.facility}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {hotspotData && (
        <>
          {/* Summary Cards */}
          <div className="hotspot-summary-section">
            <div className="summary-card total-card">
              <div className="summary-card-icon">
                <TrendingUp size={24} />
              </div>
              <div className="summary-card-content">
                <div className="summary-label">Total Emissions</div>
                <div className="summary-value">
                  {formatEmissionValue(hotspotData.total_emissions)}
                  <span className="summary-unit">kg CO₂e</span>
                </div>
              </div>
            </div>

            <div className="summary-card scenario-card">
              <div className="summary-card-icon">
                <Info size={24} />
              </div>
              <div className="summary-card-content">
                <div className="summary-label">Scenario</div>
                <div className="summary-value-small">{hotspotData.scenario_name}</div>
                <div className="summary-facility">{hotspotData.facility}</div>
              </div>
            </div>
          </div>

          {/* Emissions Summary Table */}
          <div className="emissions-summary-table-container">
            <div className="section-header">
              <h2 className="section-title">Emissions Summary by Category</h2>
              <p className="section-description">
                Overview of emissions distributed across major source categories
              </p>
            </div>

            <div className="summary-table-card">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Energy Source</th>
                    <th className="text-left">Emissions Share (kg CO₂e)</th>
                    <th className="text-right">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCategories.map(([key, category]) => (
                    <tr key={key} className="summary-table-row">
                      <td className="category-name-cell">
                        <div className="category-name-content">
                          <div 
                            className="category-color-dot" 
                            style={{ backgroundColor: CATEGORY_COLORS[key] }}
                          />
                          <span>{category.name}</span>
                        </div>
                      </td>
                      <td className="text-right emissions-value">
                        {formatEmissionValue(category.total)}
                      </td>
                      <td className="text-right share-value">
                        {category.percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td className="text-right">
                      <strong>{formatEmissionValue(hotspotData.total_emissions)}</strong>
                    </td>
                    <td className="text-right">
                      <strong>100%</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Detailed Breakdown by Category */}
          <div className="detailed-breakdown-section">
            <div className="section-header">
              <h2 className="section-title">Detailed Emissions Breakdown</h2>
              <p className="section-description">
                Expand each category to view individual emission sources
              </p>
            </div>

            <div className="category-cards-container">
              {sortedCategories.map(([categoryKey, category]) => {
                const isExpanded = expandedCategories[categoryKey];
                const Icon = CATEGORY_ICONS[categoryKey] || Factory;
                const color = CATEGORY_COLORS[categoryKey];

                return (
                  <div key={categoryKey} className="category-card">
                    <button
                      className="category-header"
                      onClick={() => toggleCategory(categoryKey)}
                      style={{ borderLeftColor: color }}
                    >
                      <div className="category-header-left">
                        <Icon size={20} style={{ color }} />
                        <div className="category-header-text">
                          <h3 className="category-title">{category.name}</h3>
                          <div className="category-meta">
                            {category.items.length} emission source{category.items.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="category-header-right">
                        <div className="category-total">
                          <div className="category-total-value">
                            {formatEmissionValue(category.total)}
                            <span className="unit">kg CO₂e</span>
                          </div>
                          <div className="category-percentage" style={{ color }}>
                            {category.percentage.toFixed(1)}%
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown size={20} className="expand-icon" />
                        ) : (
                          <ChevronRight size={20} className="expand-icon" />
                        )}
                      </div>
                    </button>

                    {isExpanded && category.items.length > 0 && (
                      <div className="category-items">
                        {category.items.map((item, index) => {
                          const itemPercentage = category.total > 0 
                            ? (item.value / category.total) * 100 
                            : 0;

                          return (
                            <div key={`${item.name}-${index}`} className="emission-item">
                              <div className="emission-item-left">
                                <div className="emission-item-name">
                                  {item.name.replace(/_/g, ' ')}
                                </div>
                                <div className="emission-item-source">
                                  {item.source_category.replace(/_/g, ' ')}
                                </div>
                              </div>
                              <div className="emission-item-right">
                                <div className="emission-item-value">
                                  {formatEmissionValue(item.value)}
                                  <span className="unit">kg CO₂e</span>
                                </div>
                                <div className="emission-item-bar">
                                  <div 
                                    className="emission-item-bar-fill"
                                    style={{ 
                                      width: `${itemPercentage}%`,
                                      backgroundColor: color
                                    }}
                                  />
                                </div>
                                <div className="emission-item-percentage">
                                  {itemPercentage.toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HotspotEmissions;
